import { addRoomsToBeConnected, addToCheckoutCart, removeRoomsToBeConnected } from 'actions/CheckoutActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import type { ComponentProps } from 'react'
import { useCallback } from 'react'
import MultiRoomOfferCheckoutButton from './MultiRoomOfferCheckoutButton'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type TextButton from 'LuxKit/Button/TextButton'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { isAccommodationItem } from 'lib/checkout/checkoutUtils'
import type { PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { getActivePackagesByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { useIntl } from 'react-intl'

interface Props extends Omit<ComponentProps<typeof TextButton>, 'kind' | 'children'> {
  disabled?: boolean
  checkIn?: string
  rooms: Array<App.RoomOccupants>
  activePackagesByRoom: PackageOptionViewByRoom
}

function MultiRoomOfferBookButton(props: Props) {
  const { disabled, checkIn, rooms, activePackagesByRoom, ...rest } = props
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const goToCheckout = useCallback((items: Array<App.Checkout.AnyItem>, connectRooms: boolean, keepBrowsing: boolean) => {
    const offerId = items.find(isAccommodationItem)!.offerId
    dispatch(addToCheckoutCart(items))
    if (connectRooms) {
      dispatch(addRoomsToBeConnected(offerId))
    } else {
      dispatch(removeRoomsToBeConnected(offerId))
    }
    if (keepBrowsing) {
      showSnackbar(
        intl.formatMessage({
          id: 'UHg8fvI8-CUAkW0U-EAE',
          defaultMessage: 'Your item has been added to the cart.',
        }),
        'success',
        {
          action: {
            label: intl.formatMessage({
              id: 'ilrAsVphfLlLIlUjT4aB',
              defaultMessage: 'View cart',
            }),
            to: '/cart',
          },
        },
      )
      return
    }
    dispatch(navigateToCheckout())
  }, [dispatch, intl])

  const buttonText = checkIn
    ? intl.formatMessage(
        {
          id: '__TtOEZ4ZrjQCCLa3L5R',
          defaultMessage: 'Book {count, plural, one {# room} other {# rooms}} now',
        },
        { count: rooms.length },
      )
    : intl.formatMessage({
        id: 'piSbD3dPr2IbrHc25dEd',
        defaultMessage: 'Check availability',
      })

  return <MultiRoomOfferCheckoutButton
    {...rest}
    activePackagesByRoom={activePackagesByRoom}
    kind="primary"
    type="submit"
    onProceedToBooking={goToCheckout}
    disabled={disabled}
  >
    {buttonText}
  </MultiRoomOfferCheckoutButton>
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  rooms: state.rooms,
  activePackagesByRoom: getActivePackagesByRoom(state, appState),
}), MultiRoomOfferBookButton)
